import { useCallback, type Dispatch, type MutableRefObject, type RefObject, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { post, postStream } from '@/utils/api-handler';
import { useProtectedMutation } from '@/hooks/use-protected-mutation';
import type { ChatMessage } from '@/hooks/use-command-messages';
import type { AttachedFileMetadata, CommandSession } from '@/types/command-agent';
import type { AttachmentItem } from '@/hooks/use-file-attachment-queue';
import { ExtendedChatMessage } from './chat-types';
import { dedupeTempUserDuplicatesWithServer, markCachedMessagesCompleted } from './message-utils';
import { createEventHandler, processSSEChunk } from './sse-event-handler';

const friendlyErrorMessage = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    const candidates = [
        typeof data?.message === 'string' ? data.message : null,
        typeof data?.detail === 'string' ? data.detail : null,
        Array.isArray(data?.detail) && typeof data.detail[0]?.msg === 'string' ? data.detail[0].msg : null,
        typeof data?.error === 'string' ? data.error : null,
        typeof error?.message === 'string' && !/^(http error|request failed|network error)/i.test(error.message)
            ? error.message
            : null,
    ];
    const found = candidates.find((c) => typeof c === 'string' && c.trim().length > 0);
    return found || fallback;
};

interface SendMutationArgs {
    message: string;
    sessionId: string;
    onEvent: (event: any) => void;
    fileIds?: string[];
    messageMetadata?: { attached_files?: AttachedFileMetadata[] };
}

interface UseSendMessageArgs {
    agentId: string | null | undefined;
    currentSessionId: string | null | undefined;
    isTyping: boolean;
    streamingMessageId: string | null;
    attachedFiles: AttachmentItem[];
    /** Session-creation mutation from useCommandSessions / local hook. Awaited when there is no sessionId yet. */
    createSessionMutation: { mutate: (vars: undefined, opts: { onSuccess: (d: CommandSession) => void; onError: () => void }) => void };
    /** Clears staged attachments after a successful dispatch. */
    clearAttachments: () => void;
    setInputValue: Dispatch<SetStateAction<string>>;
    setCurrentSessionId: Dispatch<SetStateAction<string | null>>;
    setMessages: Dispatch<SetStateAction<ExtendedChatMessage[]>>;
    setIsTyping: Dispatch<SetStateAction<boolean>>;
    setStreamingMessageId: Dispatch<SetStateAction<string | null>>;
    setStreamingPhase: Dispatch<SetStateAction<'working' | 'responding'>>;
    streamCancelledRef: MutableRefObject<boolean>;
    streamAbortRef: MutableRefObject<Set<AbortController>>;
    pendingMessagesRef: MutableRefObject<ExtendedChatMessage[]>;
    stickyContextRef: MutableRefObject<any>;
    inputRef: RefObject<HTMLTextAreaElement | null>;
    onSessionTitle?: (sessionId: string, title: string) => void;
    queryClient: QueryClient;
}

/**
 * Owns the send-message round-trip: optimistic user/system messages, the streaming POST,
 * and the stop-generation control. Returns `sendMessage`, `handleStopGeneration`, and the
 * `isStopping` flag for the stop-button spinner.
 *
 * Keeps the streaming request alive across session switches — only UI updates for the prior
 * session are halted (via `streamCancelledRef`), the HTTP call finishes server-side.
 */
export function useSendMessage({
    agentId,
    currentSessionId,
    isTyping,
    streamingMessageId,
    attachedFiles,
    createSessionMutation,
    clearAttachments,
    setInputValue,
    setCurrentSessionId,
    setMessages,
    setIsTyping,
    setStreamingMessageId,
    setStreamingPhase,
    streamCancelledRef,
    streamAbortRef,
    pendingMessagesRef,
    stickyContextRef,
    inputRef,
    onSessionTitle,
    queryClient,
}: UseSendMessageArgs) {
    const stopGenerationMutation = useProtectedMutation({
        mutationFn: async ({ agentId: aid, sessionId: sid }: { agentId: string; sessionId: string }) => {
            await post(`/api/command-team/agents/${aid}/sessions/${sid}/messages/stop`, {});
            return {};
        },
        requiredPermissions: ['CREATE_COMMAND_MESSAGE'],
        onSuccess: async () => {
            // Mark streaming as done — handle both top-level and nested branch messages.
            setMessages((prev) =>
                prev.map((msg) => {
                    if (msg.branches) {
                        return {
                            ...msg,
                            branches: msg.branches.map((branch) =>
                                branch.isStreaming ? { ...branch, isStreaming: false } : branch,
                            ),
                        };
                    }
                    return msg.isStreaming ? { ...msg, isStreaming: false } : msg;
                }),
            );

            // Keep the streaming lock active while we refetch so the merge effect
            // doesn't fire with stale cache and wipe the stopped branch.
            await queryClient.refetchQueries({
                queryKey: ['command-messages', agentId, currentSessionId],
            });

            setIsTyping(false);
            setStreamingMessageId(null);

            toast.success('Generation stopped', {
                description: 'The response has been stopped. You can send a new message when ready.',
               
            });
            setTimeout(() => inputRef.current?.focus(), 0);
        },
        onError: (error: any) => {
            toast.error('Could not stop generation', {
                description: friendlyErrorMessage(error, 'Please try again.'),
            });
        },
    });

    const handleStopGeneration = useCallback(() => {
        if (!agentId || !currentSessionId || (!isTyping && !streamingMessageId)) return;
        stopGenerationMutation.mutate({ agentId, sessionId: currentSessionId });
    }, [agentId, currentSessionId, isTyping, streamingMessageId, stopGenerationMutation]);

    const sendMessageMutation = useProtectedMutation({
        mutationFn: async ({ message, sessionId, onEvent, fileIds, messageMetadata }: SendMutationArgs) => {
            const abortController = new AbortController();
            streamAbortRef.current.add(abortController);

            const body: { content: string; timezone: string; file_ids?: string[]; message_metadata?: { attached_files?: AttachedFileMetadata[] } } = {
                content: message,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
            if (fileIds?.length) body.file_ids = fileIds;
            if (messageMetadata?.attached_files?.length) body.message_metadata = messageMetadata;

            try {
                await postStream(
                    `/api/command-team/agents/${agentId}/sessions/${sessionId}/messages`,
                    body,
                    (chunk: string) => processSSEChunk(chunk, onEvent),
                    abortController.signal,
                );
            } finally {
                streamAbortRef.current.delete(abortController);
            }
            return { success: true };
        },
        requiredPermissions: ['CREATE_COMMAND_MESSAGE'],
        onSuccess: async () => {
            // Don't blanket-drop temp user rows: duplicate sends (e.g. "Hey" twice) can momentarily
            // look unconfirmed after stream end. Count-based dedupe preserves legitimate duplicates.
            setMessages((prev) => dedupeTempUserDuplicatesWithServer(prev));
            // Flip any cached GENERATING rows to COMPLETED locally so the reconnect effect
            // doesn't see a stale `generatingAssistantId` and open a second stream.
            markCachedMessagesCompleted(queryClient, agentId, currentSessionId);
            setIsTyping(false);
            setStreamingMessageId(null);
            // Refetch sessions so updated_at changes bubble the active session to the top.
            queryClient.invalidateQueries({ queryKey: ['command-sessions', agentId] });
        },
        onError: (error: any, variables: SendMutationArgs) => {
            const isAborted =
                error?.name === 'AbortError' || error?.message?.includes('aborted') || streamCancelledRef.current;
            if (isAborted) return;

            // Stale/missing session — common after switching agents while the URL still
            // carries a session_id from a different agent. Reset and transparently retry
            // through the auto-create flow so the user keeps the message they just typed.
            const status = (error as { response?: { status?: number } })?.response?.status;
            const errMsg = String(error?.message || '');
            const looksLikeMissingSession =
                status === 404 || /not found|couldn['’]t find/i.test(errMsg);

            if (looksLikeMissingSession && currentSessionId) {
                setCurrentSessionId(null);
                streamAbortRef.current.forEach((c) => {
                    try { c.abort(); } catch { /* noop */ }
                });
                streamAbortRef.current.clear();
                toast.info('Starting a new chat', {
                    description: "The previous chat couldn't be found — continuing in a new one.",
                });
                createSessionMutation.mutate(undefined, {
                    onSuccess: (sessionData: CommandSession) => {
                        if (sessionData?.id) {
                            setCurrentSessionId(sessionData.id);
                            sendMessageMutation.mutate({
                                ...variables,
                                sessionId: sessionData.id,
                            });
                            return;
                        }
                        setMessages([]);
                        pendingMessagesRef.current = [];
                        setStreamingMessageId(null);
                        setIsTyping(false);
                        toast.error("Couldn't start a new chat", {
                            description: 'The session was created but no ID was returned. Please try again.',
                        });
                    },
                    onError: () => {
                        setMessages([]);
                        pendingMessagesRef.current = [];
                        setStreamingMessageId(null);
                        setIsTyping(false);
                    },
                });
                return;
            }

            toast.error('Failed to send message', {
                description: friendlyErrorMessage(error, "We couldn't send your message. Please try again."),
            });
            setIsTyping(false);
            setStreamingMessageId(null);
            pendingMessagesRef.current = [];
            setMessages((prev) =>
                prev
                    .filter((msg) => !(msg.role === 'assistant' && msg.isStreaming && (typeof msg.content !== 'string' || msg.content.trim().length === 0) && (msg.contentSegments?.length ?? 0) === 0))
                    .map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg))
            );
        },
    });

    const sendMessage = useCallback(
        (text: string) => {
            const messageToSend = text.trim();
            const uploadedList = attachedFiles
                .filter((a) => a.status === 'done' && a.uploaded)
                .map((a) => a.uploaded!);
            if ((!messageToSend && uploadedList.length === 0) || isTyping || !agentId) return;

            const fileIds = uploadedList.map((f) => f.id);
            /** API requires non-empty `content` when files are attached; keep UI text empty so no empty bubble. */
            const contentForApi = messageToSend || (uploadedList.length > 0 ? ' ' : '');
            const contentForDisplay = messageToSend;

            setInputValue('');
            clearAttachments();
            setTimeout(() => inputRef.current?.focus(), 0);

            const userMessage: ExtendedChatMessage = {
                id: `temp-user-${Date.now()}`,
                content: contentForDisplay,
                role: 'user',
                timestamp: new Date(),
                ...(uploadedList.length > 0 && { attachedFiles: uploadedList }),
            };

            const systemMessage: ChatMessage = {
                id: `temp-system-${Date.now()}`,
                content: '',
                role: 'assistant',
                timestamp: new Date(),
                isStreaming: true,
            };

            streamCancelledRef.current = false;
            setStreamingPhase('working');
            // The SSE handler swaps the temp id for the real server id on `message_start`.
            // Track that here so finalize/error paths can still locate the row by its
            // current id instead of the stale temp one.
            let liveSystemId = systemMessage.id;
            const eventHandler = createEventHandler({
                systemMessageId: systemMessage.id,
                setMessages,
                onSessionTitle,
                cancelledRef: streamCancelledRef,
                setStreamingPhase,
                userMessageId: userMessage.id,
                onAssistantIdResolved: (realId) => { liveSystemId = realId; },
            });

            const finalizeMessage = () => {
                setMessages((prev) => {
                    const msg = prev.find((m) => m.id === liveSystemId) as ExtendedChatMessage | undefined;
                    const hasContent =
                        !!msg &&
                        (
                            (typeof msg.content === 'string' && msg.content.trim().length > 0) ||
                            (msg.contentSegments?.length ?? 0) > 0 ||
                            (msg.tools?.length ?? 0) > 0 ||
                            (typeof msg.reasoning === 'string' && msg.reasoning.trim().length > 0) ||
                            !!msg.streamError
                        );
                    if (msg && !hasContent) {
                        return prev.filter((m) => m.id !== liveSystemId);
                    }
                    return prev.map((m) => (m.id === liveSystemId ? { ...m, isStreaming: false } : m));
                });
                setStreamingMessageId(null);
                setIsTyping(false);
                setTimeout(() => inputRef.current?.focus(), 0);
            };

            requestAnimationFrame(() => {
                stickyContextRef.current?.scrollToBottom?.('instant');
            });

            if (!currentSessionId) {
                setIsTyping(true);
                pendingMessagesRef.current = [userMessage, systemMessage];
                setMessages([userMessage, systemMessage]);
                setStreamingMessageId(systemMessage.id);

                createSessionMutation.mutate(undefined, {
                    onSuccess: (sessionData: CommandSession) => {
                        if (sessionData?.id) {
                            setCurrentSessionId(sessionData.id);
                            sendMessageMutation.mutate(
                                {
                                    message: contentForApi,
                                    sessionId: sessionData.id,
                                    onEvent: eventHandler,
                                    ...(fileIds.length > 0 && { fileIds, messageMetadata: { attached_files: uploadedList } }),
                                },
                                {
                                    onSuccess: () => {
                                        pendingMessagesRef.current = [];
                                        finalizeMessage();
                                    },
                                },
                            );
                            return;
                        }
                        setMessages([]);
                        pendingMessagesRef.current = [];
                        setStreamingMessageId(null);
                        setIsTyping(false);
                        toast.error("Couldn't start a new chat", {
                            description: 'The session was created but no ID was returned. Please try again.',
                        });
                    },
                    onError: () => {
                        setMessages([]);
                        pendingMessagesRef.current = [];
                        setStreamingMessageId(null);
                        setIsTyping(false);
                    },
                });
            } else {
                setMessages((prev) => [...prev, userMessage, systemMessage]);
                setStreamingMessageId(systemMessage.id);
                setIsTyping(true);

                sendMessageMutation.mutate(
                    {
                        message: contentForApi,
                        sessionId: currentSessionId,
                        onEvent: eventHandler,
                        ...(fileIds.length > 0 && { fileIds, messageMetadata: { attached_files: uploadedList } }),
                    },
                    {
                        onSuccess: () => {
                            finalizeMessage();
                        },
                    },
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isTyping, agentId, currentSessionId, sendMessageMutation, createSessionMutation, onSessionTitle, attachedFiles, queryClient],
    );

    return {
        sendMessage,
        handleStopGeneration,
        isStopping: stopGenerationMutation.isPending,
    };
}
